import React, { useEffect, useState} from 'react'
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useRequest from '../../components/hooks/use-req';
import { showToast } from '../../components/toast';
import Button from '../../components/button';
import { ClockLoader } from 'react-spinners';
import { Eye, EyeOff } from 'lucide-react';
import { LockIcon, MailIcon, SignIcon, WhatsappIcon } from '../../assets/icon';
import Input from '../../components/input';
import Card from '../../components/card';
import {
  clearLoginLockout,
  getLoginLockout,
  isPreLoginPath,
  LOGIN_MAX_ATTEMPTS,
  LoginLockoutEntry,
  recordFailedLogin,
} from '../../utils/function';

interface LoginFormProps {
  onSuccess?: () => void;
  isModal?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, isModal }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState<string>('');
  const navigate = useNavigate();
  const [usePhoneLogin, setUsePhoneLogin] = useState(false);
  const [userType, setUserType] = useState<"recommend" | "advertise">("recommend");
  const [rememberMe, setRememberMe] = useState(false);
  const [lockout, setLockout] = useState<LoginLockoutEntry>({ failCount: 0, lockedUntil: null });
  // Shown inline under the password field, not as a toast — see the render block below.
  const [loginError, setLoginError] = useState<string | null>(null);
  const { handleSubmit, control, resetField, reset, setValue } = useForm();
  const { loading, makeRequest } = useRequest("/auth/auth/login", "POST");
  const { loading: statusLoading, makeRequest: statusCheck } = useRequest("/auth/auth/registration-status", "POST");

  const location = useLocation();

  const isLocked = !!lockout.lockedUntil && lockout.lockedUntil > Date.now();
  const lockedMinutesLeft = isLocked ? Math.ceil(((lockout.lockedUntil as number) - Date.now()) / 60000) : 0;

  // Re-check (and refresh) the lockout state for the identifier currently being signed in
  // with — once the password step is shown, and every 30s afterward so the "try again in Xm"
  // message counts down and the form re-enables itself once the lockout expires, without
  // needing a page refresh.
  useEffect(() => {
    if (!showPasswordField || !userIdentifier) return;

    setLockout(getLoginLockout(userIdentifier));
    const interval = setInterval(() => setLockout(getLoginLockout(userIdentifier)), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPasswordField, userIdentifier]);

  // Step 1: Check registration status with email or phone
  const handleStatusCheck = handleSubmit(async (formData) => {
    // Prevent multiple submissions
    if (statusLoading) return;

    // Construct payload based on login method
    const payload = usePhoneLogin
      ? { phone: formData.data }
      : { email: formData.data };

    try {
      const [response] = await statusCheck(payload);

      if (response?.status) {
        // Three-way decision based on user_exists / fully_registered / password_set:
        // - user_exists === false -> account doesn't exist yet, go create one.
        // - fully_registered && password_set (both true) -> continue straight to the
        //   password step.
        // - user_exists === true but not both of the above -> send to /onboarding to finish
        //   setup (covers is_migrated accounts too, e.g. fully_registered: true but
        //   password_set: false since they never set a password in the new system).
        if (response?.user_exists === false) {
          showToast(response?.message || "Account not found. Please create an account first.", false);
          if (!isModal) {
            setTimeout(() => navigate("/signup", { replace: true }), 1000);
          }
          return;
        }

        if (response?.fully_registered === true && response?.password_set === true) {
          setUserIdentifier(formData.data);
          setShowPasswordField(true);
          showToast("Please enter your password", true);
          return;
        }

        // User needs to complete registration - redirect to onboarding
        localStorage.setItem(
          "userDetails",
          JSON.stringify({
            user_id: response?.user_id,
            first_name: response?.first_name,
            last_name: response?.last_name,
            email: response?.email,
            phone: response?.phone,
            missing_fields: response?.missing_fields
          })
        );
        showToast(response?.message, true);

        localStorage.setItem(
          "onboardingPrefill",
          JSON.stringify({
            email: usePhoneLogin ? "" : formData.data,
            phone: usePhoneLogin ? formData.data : "",
          })
        );

        if (isModal) {
          onSuccess?.();
        }
        setTimeout(() => navigate("/onboarding", { replace: true }), 500);
      } else if (response?.data?.in_waitlist) {
        // User is in waitlist - redirect to onboarding
        localStorage.setItem(
          "userDetails",
          JSON.stringify(response?.data?.waitlist_details)
        );
        showToast(response?.message, true);
        setExistingUser(true);

        if (isModal) {
          onSuccess?.();
        } else {
          setTimeout(() => navigate("/signup", { replace: true }), 500);
        }
        return;
      } else {
        // User doesn't exist - redirect to signup/setup
        showToast("Account not found. Please create an account first.", false);
        if (!isModal) {
          setTimeout(() => navigate("/setup", { replace: true }), 1000);
        }
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const errorMessage =
        typeof detail === "string"
          ? detail
          : detail?.message ||
          detail?.error?.details?.[0]?.message ||
          "An unexpected error occurred.";

      showToast(errorMessage, false);
    }
  });

  // Step 2: Handle actual login with password
  const handleLogin = handleSubmit(async (formData) => {
    if (loading) return;
    setLoginError(null);

    // Client-side throttle only — see the note above LOGIN_MAX_ATTEMPTS in utils/function.tsx
    // for why this doesn't by itself close the "no account lockout" finding.
    const currentLockout = getLoginLockout(userIdentifier);
    if (currentLockout.lockedUntil && currentLockout.lockedUntil > Date.now()) {
      setLockout(currentLockout);
      return;
    }

    const loginPayload = {
      data: userIdentifier,
      password: formData.password,
    };

    try {
      const [response, error] = await makeRequest(loginPayload);

      // ================= HANDLE HTTP ERRORS =================
      if (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Login failed";

        const updatedLockout = recordFailedLogin(userIdentifier);
        setLockout(updatedLockout);
        // Once locked, the persistent lockout banner below covers it — no need for a
        // separate message here too.
        if (!updatedLockout.lockedUntil) {
          setLoginError(message);
        }
        return;
      }

      // ================= HANDLE BUSINESS LOGIC FAILURE =================
      if (!response?.status) {
        const updatedLockout = recordFailedLogin(userIdentifier);
        setLockout(updatedLockout);
        if (!updatedLockout.lockedUntil) {
          const remaining = LOGIN_MAX_ATTEMPTS - updatedLockout.failCount;
          setLoginError(
            `${response?.message || "We couldn't sign you in with those details. Check your credentials and try again."} (${remaining} attempt${remaining === 1 ? '' : 's'} left before temporary lockout)`
          );
        }
        return;
      }

      // Successful login clears any prior failed-attempt count for this identifier.
      clearLoginLockout(userIdentifier);

      // ================= WAITLIST FLOW =================
      const isWaitlisted = response?.data?.in_waitlist;

      if (isWaitlisted) {
        localStorage.setItem(
          "userDetails",
          JSON.stringify(response?.data?.waitlist_details)
        );

        showToast(response?.message, true);
        setExistingUser(true);

        if (isModal) {
          onSuccess?.();
        } else {
          setTimeout(() => navigate("/signup", { replace: true }), 500);
        }

        return;
      }

      // ================= NORMAL LOGIN =================
      const user = response?.data?.user;
      const userTypeValue = user?.user_type;

      localStorage.setItem("userDetails", JSON.stringify(user));
      localStorage.setItem("authToken", response?.data?.access_token);
      localStorage.setItem("user-type", userTypeValue);

      // Login can happen from deep inside the tree (e.g. a reward modal's own LoginModal),
      // far from MainHeader — a state change there doesn't cascade up to re-render MainHeader,
      // so it keeps showing "Sign In" with the stale pre-login authToken read until *something*
      // forces it to re-render. Broadcast this so MainHeader (and anything else watching) can
      // refresh immediately, regardless of where in the tree the login actually happened.
      window.dispatchEvent(new Event("auth-changed"));

      showToast(response?.message, true);

      if (isModal) {
        onSuccess?.();
        return;
      }

      setTimeout(() => {
        // Always drop a stale value here regardless of whether we end up using it — a bad
        // pre-login path sitting in storage should never survive a successful login.
        const storedRedirect = localStorage.getItem("redirectAfterLogin");
        localStorage.removeItem("redirectAfterLogin");

        // Never redirect back into a pre-login page (e.g. "/login" itself, saved by some
        // unrelated background request that 401'd while the user was sitting on this page) —
        // that's a no-op that looks like the login silently failed.
        const redirectPath =
          location.state?.from ||
          (storedRedirect && !isPreLoginPath(storedRedirect) ? storedRedirect : null);

        if (redirectPath) {
          navigate(redirectPath, { replace: true });
          return;
        }

        switch (userTypeValue) {
          case "SUPER_ADMIN":
            navigate("/admin/overview", { replace: true });
            break;
          case "ADVERTISER":
            navigate("/business/profile", { replace: true });
            break;
          case "RECOMMENDER":
          default:
            navigate("/", { replace: true });
            break;
        }
      }, 100);
    } catch (err: any) {
      // ================= FALLBACK SAFETY =================
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong";

      setLoginError(message);
    }
  });

  // Reset to step 1 when switching between email/phone
  const handleSwitchLoginMethod = () => {
    setUsePhoneLogin(!usePhoneLogin);
    setShowPasswordField(false);
    setUserIdentifier('');
    setLoginError(null);
    resetField("data");
    resetField("password");
    setValue("password", "");
  };

  const getSignInIcon = () => {
    if (userType === "advertise") {
      return (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="48" height="48" rx="24" fill="#6661FF" />
          <path d="M32.6154 17.5939V26.7134C32.6158 26.9562 32.5583 27.1959 32.4475 27.4142C32.3366 27.6325 32.1752 27.8236 31.9757 27.9729C31.6819 28.1879 31.3232 28.3054 30.9538 28.3077C30.8193 28.3073 30.6854 28.2912 30.555 28.2599L22.6458 26.3626L21.7153 26.1394H20.1534C19.066 26.1393 18.022 25.73 17.2456 24.9995C16.6565 24.4453 16.2528 23.7353 16.0859 22.9601C15.919 22.1849 15.9964 21.3795 16.3084 20.6467C16.6203 19.9139 17.1525 19.2868 17.8373 18.8453C18.5221 18.4038 19.3284 18.168 20.1534 18.1679H21.7153L30.555 16.0475C30.799 15.989 31.0536 15.9845 31.2997 16.0342C31.5458 16.0839 31.7769 16.1866 31.9757 16.3344C32.1752 16.4838 32.3366 16.6749 32.4475 16.8932C32.5583 17.1114 32.6158 17.3511 32.6154 17.5939Z" fill="white" />
          <path d="M22.769 27.4458V30.399C22.769 30.8235 22.6017 31.2306 22.3039 31.5308C22.0061 31.831 21.6021 31.9996 21.1809 31.9996H20.244C19.8713 32.0068 19.508 31.8817 19.2177 31.6461C18.9274 31.4105 18.7285 31.0796 18.6559 30.7111L17.8459 26.4614C18.2704 26.7353 18.7374 26.9354 19.2276 27.0537C19.6065 27.1502 19.996 27.1986 20.3869 27.1977H21.7844L22.769 27.4458Z" fill="white" />
        </svg>
      );
    }
    return <SignIcon />;
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-6">
        {getSignInIcon()}
        {isModal ? (
          <span className="font-medium text-gray-900">
            Sign in to your account to generate <br /> VouchLinks
          </span>
        ) : (
          <span className="font-medium text-gray-900">
            {showPasswordField ? "Enter your password" : "Sign in"}
          </span>
        )}
      </div>

      <form onSubmit={showPasswordField ? handleLogin : handleStatusCheck}>
        {/* EMAIL FIELD */}
        {!usePhoneLogin && (
          <Controller
            name="data"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            }}
            render={({ field, fieldState }) => (
              <Input
                label="Email Address"
                placeholder="your@email.com"
                value={field.value}
                error={fieldState?.error?.message}
                onChange={field.onChange}
                className="w-full mb-3"
                icon={<MailIcon />}

              />
            )}
          />
        )}

        {/* PHONE FIELD */}
        {usePhoneLogin && (
          <Controller
            name="data"
            control={control}
            rules={{
              required: "WhatsApp Number is required",
              pattern: {
                value: /^\+234[0-9]{10}$/,
                message: "Enter a valid WhatsApp number",
              },
            }}
            render={({ field, fieldState }) => {
              const currentPhone = field.value ? field.value.replace("+234", "") : "";
              return (
                <Input
                  type="tel"
                  label="WhatsApp Number"
                  placeholder="8012345678"
                  icon={<WhatsappIcon />}
                  prefix="+234"
                  value={currentPhone}
                  error={fieldState?.error?.message}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    if (val?.length <= 10) {
                      field.onChange(`+234${val}`);
                    }
                  }}
                  autoComplete="off"
                  className="w-full mb-3"

                />
              );
            }}
          />
        )}

        {/* PASSWORD FIELD - Only shown after status check */}
        {showPasswordField && (
          <div className="mb-4">
            <div className="relative">
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                }}
                render={({ field, fieldState }) => (
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    label='Password'
                    placeholder="Your password"
                    icon={<LockIcon />}
                    value={field.value}
                    error={fieldState?.error?.message}
                    onChange={field.onChange}
                    className="w-full"
                  />
                )}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                type="button"
                className="absolute right-3 top-14 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
        )}

        {/* Login failure feedback — shown inline right under the password field, not as a
            toast at the top of the screen. Lockout (a harder stop) takes priority over a
            plain wrong-credentials message when both would otherwise apply. */}
        {showPasswordField && isLocked && (
          <div className="flex gap-3 items-start rounded-[8px] mb-4 text-[14px] p-3 bg-red-50 text-red-700 border border-red-100">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" fill="#DC2626" />
              <path d="M12 7v6M12 16.5h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p>Too many failed attempts. Try again in {lockedMinutesLeft} minute{lockedMinutesLeft === 1 ? '' : 's'}.</p>
          </div>
        )}
        {showPasswordField && !isLocked && loginError && (
          <div className="flex gap-3 items-start rounded-[8px] mb-4 text-[14px] p-3 bg-gray-100 text-[#121212]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" fill="#F97316" />
              <path d="M12 7v6M12 16.5h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p>{loginError}</p>
          </div>
        )}

        {/* Remember Me & Forgot Password - Only shown when password field is visible */}
        {showPasswordField && (
          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="text-sm text-[#333D4D] font-medium">
                Remember me
              </span>
            </label>
            <button type='button' onClick={() => navigate('/forgotpassword')} className="text-sm text-[#333D4D] hover:text-gray-600 font-medium">
              Forgot Password?
            </button>
          </div>
        )}

        {existingUser && (
          <div className="flex gap-3 rounded-[8px] my-5 text-[14px] p-3 bg-[#1212121A] text-[#121212]">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z"
                fill="#00A86B"
              />
            </svg>
            <p>
              Hey, we know you! Signing you in but create your password first.
            </p>
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          className="w-full mb-3"
          type="submit"
          disabled={loading || statusLoading || (showPasswordField && isLocked)}
        >
          {loading || statusLoading ? (
            <ClockLoader color="#ffffff" loading={loading || statusLoading} size={20} />
          ) : (
            <>
              {showPasswordField
                ? "Sign In"
                : userType === "advertise"
                  ? "Continue as Advertiser"
                  : "Continue"}
            </>
          )}
        </Button>

        {/* Only show "Or" and alternative login method when on first step */}
        {!showPasswordField && (
          <>
            <p className="text-center font-semibold mb-3">
              Or
            </p>

            <button
              type="button"
              onClick={handleSwitchLoginMethod}
              className="w-full border-2 text-[#333D4D] text-[16px] border-[#121212] p-3 font-semibold rounded-full shadow"
              disabled={loading || statusLoading}
            >
              {usePhoneLogin ? "Continue with Email" : "Continue with WhatsApp Number"}
            </button>
          </>
        )}

        <Link to={'/signup'} className="text-center flex justify-center font-medium text-sm mt-2 text-[#121212]">
          New to Vouchlinks? {' '}
          <button type="button" className="underline">
            Get Started
          </button>
        </Link>
      </form>
    </Card>
  )
}

export default LoginForm